import { useCallback, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { shipTypeData } from "src/mock/selectOptions";
import { useDispatch, useSelector } from "src/redux/hooks";
import { setShipType } from "src/redux/slices/generatorSlice";

import { resolveTargetId } from "../../hooks/useCopyVal";
import CustomSelect from "../CustomSelect/CustomSelect";
import { getOtherFillValue, vtypeToShipType } from "./ShipTypeSelect.utils";

interface IShipType {
  id: string;
  value: string;
  required: boolean;
  target?: string;
  cols?: Record<string, any>;
}

const ShipType = ({ id, value, required, target, cols }: IShipType) => {
  const dispatch = useDispatch();
  const { getValues, setValue } = useFormContext();
  const vtype = useSelector(state => state.generatorSlice.yachtData?.vtype);
  const vtypeother = useSelector(
    state => state.generatorSlice.yachtData?.vtypeother,
  );

  const fillOtherIfNeeded = useCallback(
    (key?: string) => {
      const targetId = resolveTargetId(target, cols);
      if (!targetId) return;

      const currentTargetValue = getValues(targetId);
      const fillValue = getOtherFillValue(key, currentTargetValue, vtypeother);
      if (fillValue === undefined) return;

      setValue(targetId, fillValue);
    },
    [cols, getValues, setValue, target, vtypeother],
  );

  const additionalOnChange = useCallback(
    (newValue: string) => {
      dispatch(setShipType(newValue));
      fillOtherIfNeeded(newValue);
    },
    [dispatch, fillOtherIfNeeded],
  );

  // Default from Yacht Setup vtype when empty; sync redux shipType for greying on load.
  useEffect(() => {
    const currentValue = getValues(id);
    const currentKey =
      typeof currentValue === "string" ? currentValue : currentValue?.key;

    if (currentKey) {
      dispatch(setShipType(currentKey));
      fillOtherIfNeeded(currentKey);
      return;
    }

    const defaultKey = vtypeToShipType(vtype);
    if (!defaultKey) return;

    const item = shipTypeData.find(i => i.value === defaultKey);
    if (!item) return;

    setValue(id, { key: item.value, value: item.label });
    dispatch(setShipType(item.value));
    fillOtherIfNeeded(item.value);
  }, [dispatch, fillOtherIfNeeded, getValues, id, setValue, vtype]);

  return (
    <CustomSelect
      id={id}
      value={value}
      required={required}
      data={shipTypeData}
      onChange={additionalOnChange}
    />
  );
};

export default ShipType;
